import React, { useState, useContext } from "react";
import { AppContext } from "components/context/AppContext";
import { useNavigate } from "react-router-dom";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import { SectionHeading, Subheading as SubheadingBase } from "components/misc/Headings.js";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons.js";
import IllustrationSrc from "images/demo/booking-online.png";
import Dropdown from 'react-dropdown';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import PropTypes from "prop-types";
import { useAmenities } from "hooks/useAmenities.js";
import LoadingSpinner from "components/misc/LoadingSpinner.js";
import { ROUTE_PATHS } from "utils/routes";

const SpinnerOverlay = styled.div`
  position: fixed;
  top: 0; left: 0;
  width: 100vw; height: 100vh;
  background: rgba(255,255,255,0.7);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const Container = tw.div`relative`;
const TwoColumn = tw.div`flex flex-col md:flex-row justify-between max-w-screen-xl mx-auto py-20 md:py-24`;
const Column = tw.div`w-full max-w-md mx-auto md:max-w-none md:mx-0`;
const ImageColumn = tw(Column)`md:w-5/12 flex-shrink-0 h-80 md:h-auto`;
const TextColumn = styled(Column)(props => [
  tw`md:w-7/12 mt-16 md:mt-0`,
  props.textOnLeft ? tw`md:mr-12 lg:mr-16 md:order-first` : tw`md:ml-12 lg:ml-16 md:order-last`
]);
const Image = styled.div(props => [
  `background-image: url("${props.imageSrc}");`,
  tw`rounded bg-contain bg-no-repeat bg-center h-full`,
]);
const TextContent = tw.div`lg:py-8 text-center md:text-left`;

const Subheading = tw(SubheadingBase)`text-center md:text-left`;
const Heading = tw(SectionHeading)`mt-4 font-black text-left text-3xl sm:text-4xl lg:text-5xl text-center md:text-left leading-tight`;
const Description = tw.p`mt-4 text-center md:text-left text-sm md:text-base lg:text-lg font-medium leading-relaxed text-secondary-100`
const Form = tw.form`mt-8 md:mt-10 text-sm flex flex-col max-w-sm mx-auto md:mx-0`
const SubmitButton = tw(PrimaryButtonBase)`inline-block mt-8`;

const CustomDropdown = tw(Dropdown)`mt-6 first:mt-0 border-b-2 py-3 focus:outline-none font-medium transition duration-300 hocus:border-primary-500`;
const CustomDatePicker = styled(DatePicker)`
  ${tw`mt-6 border-b-2 py-3 w-full focus:outline-none font-medium transition duration-300`}
  border-color: #e2e8f0;
  &:focus {
    border-color: #3b82f6;
  }
`;

const AmenitiesForm = ({
  subheading = "",
  heading = <> Reservación de <span className="text-primary-500">Amenidades</span></>,
  description = "Llena correctamente el siguiente formulario escogiendo cudadosamente el tipo de amenidad y la fecha deseada.",
  submitButtonText = "Reservar",
  formAction = "#",
  formMethod = "post",
  textOnLeft = true,
}) => {
  const [availablesHours, setAvailablesHours] = useState([]);
  const [availableHoursForDay, setAvailableHoursForDay] = React.useState(availablesHours);
  const [selectedSlot, setSelectedSlot] = React.useState(null);
  const [selectedDate, setSelectedDate] = React.useState(null);
  const defaultOption = 'Alberca';
  const [selectedAmenity, setSelectedAmenity] = React.useState(defaultOption);
  const [selectedHour, setSelectedHour] = React.useState(null);
  const { amenities, loading, reservedAmenities, getReservedAmenitiesByType, reserveAmenity } = useAmenities();
  const { state } = useContext(AppContext);
  const navigate = useNavigate();

  React.useEffect(() => {
    setAvailablesHours(amenities.availablesHours);
  }, [amenities.availablesHours]);

  // Update available hours for selected day
  React.useEffect(() => {
    if (!selectedDate) {
      setAvailableHoursForDay(availablesHours);
      return;
    }
    const d = selectedDate.toISOString().slice(0, 10);
    const found = reservedAmenities.find(r => r.date === d);
    let reservedHours = found ? found.hours : [];
    // Only show hours not reserved
    setAvailableHoursForDay(availablesHours.filter(h => !reservedHours.includes(h.value)));
  }, [selectedDate, availablesHours, reservedAmenities]);

  const handleDropdownAmenities = (event) => {
    setSelectedAmenity(event.label);
    const getReserved = async () => { await getReservedAmenitiesByType(event.label) };
    getReserved();
  };

  const handleDropdownSlots = (event) => {
    setSelectedSlot(event.value);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleDropdownHour = (event) => {
    setSelectedHour(event.value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    // Collect form data
    const amenity = {
      category: selectedAmenity,
      slot: selectedSlot,
      date: selectedDate ? selectedDate.toISOString().slice(0, 10) : null,
      hour: selectedHour,
      houseNumber: state.user.homeownerId
    };
    console.log(amenity);

    await reserveAmenity(amenity).then(async (response) => {
      if (response.success) {
        navigate(ROUTE_PATHS.AMENITIES);
      }
      else {
        console.log("Error al reservar amenidad: " + response.message);
      }
    }).catch((error) => {
      console.log("Error al reservar amenidad: " + error);
    });
  };

  // Calendar logic
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();

  // Disable reserved dates and filter by slot
  const isFullyReserved = (date) => {
    const d = date.toISOString().slice(0, 10);
    const found = reservedAmenities.find(r => r.date === d);
    // If all hours are reserved for that day, disable the date
    return found && found.hours.length === availablesHours.length;
  };

  const isSlotAllowed = (date) => {
    if (!selectedSlot) return true;
    const day = date.getDay(); // 0=Sun, 1=Mon, ...
    if (selectedSlot === 1) return day >= 1 && day <= 5; // Entre Semana
    if (selectedSlot === 2) return day === 6; // Sábado
    if (selectedSlot === 3) return day === 0; // Domingo
    return true;
  };

  const filterDate = (date) => {
    // Only current month, not before today, not fully reserved, and slot allowed
    return (
      date.getMonth() === currentMonth &&
      date.getFullYear() === currentYear &&
      date >= today &&
      !isFullyReserved(date) &&
      isSlotAllowed(date)
    );
  };

  if (loading) {
    return (
      <SpinnerOverlay>
        <LoadingSpinner />
      </SpinnerOverlay>
    );
  }

  return (
    <Container id="form" name="form">
      <TwoColumn>
        <ImageColumn>
          <Image imageSrc={IllustrationSrc} />
        </ImageColumn>
        <TextColumn textOnLeft={textOnLeft}>
          <TextContent>
            {subheading && <Subheading>{subheading}</Subheading>}
            <Heading>{heading}</Heading>
            {description && <Description>{description}</Description>}
            <Form action={formAction} onSubmit={handleSubmit} >
              <CustomDropdown id="category" name="category" required controlClassName='customDropdown' options={amenities.amenities} onChange={handleDropdownAmenities} value={selectedAmenity} />
              <CustomDropdown id="slots" name="slots" required controlClassName='customDropdown' options={amenities.slots} onChange={handleDropdownSlots} value={selectedSlot ? amenities.slots.find(s => s.value === selectedSlot) : 'Selecciona Slot'} />
              <CustomDatePicker
                selected={selectedDate}
                onChange={handleDateChange}
                filterDate={filterDate}
                minDate={today}
                dateFormat="dd/MM/yyyy"
                placeholderText="Selecciona una fecha"
                showPopperArrow={false}
                required
              />
              <CustomDropdown id="hour" name="hour" required controlClassName='customDropdown' options={availableHoursForDay} onChange={handleDropdownHour} value={selectedHour ? availableHoursForDay.find(h => h.value === selectedHour) : 'Selecciona Horario'} />
              <SubmitButton type="submit">{submitButtonText}</SubmitButton>
            </Form>
          </TextContent>
        </TextColumn>
      </TwoColumn>
    </Container>
  );
}

AmenitiesForm.propTypes = {
  subheading: PropTypes.string,
  heading: PropTypes.node,
  description: PropTypes.string,
  submitButtonText: PropTypes.string,
  formAction: PropTypes.string,
  formMethod: PropTypes.string,
  textOnLeft: PropTypes.bool,
};

export default AmenitiesForm;